import { useApolloClient } from "@apollo/client";
import { Text } from "@mantine/core";
import {
  CategoryOptionType,
  GetPrivateSessionPageDocument,
  GetPrivateSessionPageQuery,
  GetPrivateSessionPageQueryVariables,
  PrivateSession,
  useUpdatePrivateSessionMutation,
} from "src/generated/graphql";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ORDER, EMPLOYEE_ROLE_OBJ, ROUTES } from "src/utils/constants";
import { Button } from "@mantine/core";
import { useModals } from "@mantine/modals";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import PrivateSessionDetail from "../PrivateSessionDetail";
import { useToggle } from "@mantine/hooks";
import { useAppContext } from "src/api/AppContext";
import useWritePermission from "src/hooks/usePermission";
import useCustomModal from "src/hooks/useCustomModal";

export type PrivateSessionData = ArrayElement<
  GetPrivateSessionPageQuery["privateSessionPage"]["items"]
>;

const PrivateSessionList = () => {
  const modals = useModals();
  const navigate = useNavigate();
  const { query } = useApolloClient();
  const {
    refetchTableData,
    setrefetchTableData,
    ShowActiveStatus,
    GridOptions,
  } = useCustomModal();
  const { currentOrganization, currentLocation } = useAppContext();
  const isLocationService =
    window.location.pathname === ROUTES.LOCATION_SERVICE;
  const isAllowWrite = useWritePermission(
    isLocationService
      ? EMPLOYEE_ROLE_OBJ.locServices
      : EMPLOYEE_ROLE_OBJ.orgService
  );

  const getPrivateSession = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<PrivateSession>> => {
    let { data } = await query<
      GetPrivateSessionPageQuery,
      GetPrivateSessionPageQueryVariables
    >({
      query: GetPrivateSessionPageDocument,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.PrivateSession_name,
          filters: isLocationService
            ? [
                ...(fetchParams.filters || []),
                {
                  field: "locationId",
                  type: "text",
                  value: currentLocation!.id,
                  parentRelationField: "rooms",
                },
              ]
            : fetchParams.filters,
        },
      },
    });

    if (data.privateSessionPage) {
      return {
        rowCount: data.privateSessionPage.totalRows,
        rowData: data.privateSessionPage.items as PrivateSession[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  const handleNavigation = (id: string): void => {
    navigate(
      `${isLocationService ? ROUTES.LOCATION_SESSION : ROUTES.SESSION}/${id}`
    );
  };

  const handleModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Private Session",
      children: (
        <PrivateSessionDetail
          isLocationSession={isLocationService}
          isEdit={false}
          refetchData={setrefetchTableData}
        />
      ),
    });
  }, [isLocationService]);

  const [updateSession] = useUpdatePrivateSessionMutation({
    onCompleted: () => {
      setrefetchTableData(!refetchTableData);
    },
  });
  const onUpdate = (id: string, isActive: boolean) => {
    updateSession({ variables: { id, data: { isActive } } });
  };

  const ShowTable = useCallback(() => {
    return (
      <ScrollableDataGrid<PrivateSessionData>
        topMargin={56}
        columnConfigs={[
          {
            field: "PrivateSession_name",
            name: "Name",
            filter: {
              field: "name",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem.id)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.name}
              </Text>
            ),
          },

          {
            field: "serviceType_name",
            name: "Type",
            filter: {
              field: "serviceType.name",
              type: "select",
              options:
                currentOrganization?.categoryOptions
                  .filter((e) => e.type === CategoryOptionType.Service)
                  .map((e) => e.name) || [],
              parentRelationField: "serviceType",
            },
            sortable: true,
            cellRenderer: ({ serviceType }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {serviceType.name}
              </Text>
            ),
          },
          {
            field: "revenueCategory_name",
            name: "Revenue",
            filter: {
              field: "revenueCategory.name",
              type: "select",
              options:
                currentOrganization?.categoryOptions
                  .filter((e) => e.type === CategoryOptionType.Revenue)
                  .map((e) => e.name) || [],
              parentRelationField: "revenueCategory",
            },
            sortable: true,
            cellRenderer: ({ revenueCategory }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {revenueCategory.name}
              </Text>
            ),
          },
          {
            field: "Duration",
            name: "Duration",
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem.id)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.minDuration} - {rowItem.maxDuration}
              </Text>
            ),
          },
          {
            field: "privateSession_isActive",
            name: "Active",
            sortable: true,
            cellRenderer: ({ isActive }) => ShowActiveStatus(isActive),
          },
          ...(isAllowWrite
            ? [
                {
                  field: "",
                  name: "",
                  cellRenderer: ({ id, isActive }: PrivateSessionData) => (
                    <GridOptions
                      id={id}
                      onUpdate={onUpdate}
                      isActive={isActive}
                    />
                  ),
                },
              ]
            : []),
        ]}
        dataFetch={getPrivateSession}
      />
    );
  }, [
    refetchTableData,
    currentOrganization?.categoryOptions,
    isLocationService,
    isAllowWrite,
    currentLocation,
  ]);

  return (
    <div>
      {isAllowWrite && (
        <Button style={{ marginBottom: 20 }} onClick={handleModal}>
          Create +
        </Button>
      )}

      <ShowTable />
    </div>
  );
};

export default PrivateSessionList;
